<template>
  <div class="app-body">
    <Header></Header>
    <main class="container-fluid">
      <div class="col-sm-12 col-md-6 offset-md-3 col-text">
        <h1>Terms Of Service</h1>
        <p><i>Effective Since 4 July 2023</i></p>

        <p>This page provides an overview of GanttProject Cloud Terms of Service during the
          beta testing period. They may change when GanttProject
          Cloud graduates from beta. We will notify users who are involved into the beta testing about the changes, if any,
          by all possible means.</p>

        <h4 id="definitions">Definitions</h4>
        <dl>
          <dt>The Website</dt>
          <dd><p>The "Website" refers to GanttProject Cloud web site, located at <a href="https://ganttproject.cloud">ganttproject.cloud</a>,
            with all its subdomains, and all content and services.</p>
          </dd>

          <dt>Service</dt>
          <dd><p>The “Service” refers to the applications, software, products, and services provided by GanttProject Cloud.</p></dd>

          <dt>"GanttProject Cloud", "We" and "Us"</dt>
          <dd><p>"GanttProject Cloud", "We" and "Us" refer to BarD Software s.r.o., a privately held company
            registered in Czech Republic, as well as our affiliates, directors, subsidiaries, contractors, licensors,
            officers, agents, and employees.</p></dd>

          <dt>“The User,” “You,” and “Your”</dt>
          <dd><p>“The User,” “You,” and “Your” refer to the individual person, company, or organization that has visited or is
            using the Website or Service; that accesses or uses any part of the account; or that directs the use of the
            account in the performance of its functions.</p></dd>

          <dt>Your Content</dt>
          <dd>Your Content is any text or binary data which You make available to us by means of Website or other parts
            of Service and which is stored under any team owned by you.</dd>
        </dl>

        <h4 id="account">Account Terms</h4>
        <dl>
          <dt>Be human at age &gt; 13</dt>
          <dd><p>You must be a human to create an account. Accounts registered by "bots" or other automated methods are not permitted.</p>
            <p>You must be at least 13, or in some cases, even older. Please check your local law for the age of digital consent. If you don’t meet these age requirements, you may not use the Service.</p>
          </dd>

          <dt>Keep your account secure</dt>
          <dd>
            <p>You are responsible for all content posted and activity that occurs under your account. You are
              responsible
              for maintaining the security of your account and password, if any. We cannot and will not be liable
              for any
              loss or damage from your failure to comply with this security obligation.
              You will promptly <a :href="routes.ABOUT_CONTACTS">notify us</a>
              if you become aware of any unauthorized use of, or access to, our Service
              through your account, including any unauthorized use of your password or account.</p>
          </dd>
        </dl>


        <h4 id="privacy">Privacy Policy</h4>
        <p>By registering on GanttProject Cloud and using it you provide us with different pieces
          of your personal information and express your consent to collect, store, process and disclose
          your personal information as described in our <a :href="routes.ABOUT_PRIVACY">Privacy Policy</a></p>

        <h4 id="use">Acceptable Use</h4>
        <dl>
          <dt>Do not violate the law and be nice to others</dt>
          <dd><p>
            Your use of the Website and Service must not violate any applicable laws, including copyright or trademark laws,
            export control laws, or other laws in your jurisdiction, and must not be harmful to others.
            Harmful use includes, but is not limited to</p>
            <ul>
              <li>engaging in illegal or fraudulent activities;</li>
              <li>infringing upon others' intellectual property rights;</li>
              <li>distributing harmful or offensive content that is defamatory, obscene, abusive;</li>
              <li>violating the security or integrity of any computer, network or communications system;</li>
              <li>impersonating any person or entity, including any of our employees or representatives;</li>
              <li>violating the privacy of any third party, such as by posting another
                person's personal information without consent;</li>
              <li>excessive use of computational and storage resources with activities
                unrelated to the primary purpose of Service;</li>
            </ul>

            <p>You are responsible for making sure that your use of the Service is in
              compliance with laws and any applicable regulations.</p>
          </dd>
        </dl>

        <h4 id="content">User-Generated Content</h4>
        <dl>
          <dt>Content ownership</dt>
          <dd>
            <p>
              You retain ownership of and responsibility for Your Content.
              You agree that you are responsible
              for any content you post; that you will only submit content that you have
              the right to post; and that you will fully comply with any third party
              licenses relating to content you post.
            </p>
          </dd>

          <dt>License grant to us</dt>
          <dd>
            <p>
              We need the legal right to host, store and process Your Content.
              You grant us, our legal successors and our trusted business partners
              the right to store, parse, and display Your Content, transform it,
              make incidental copies as necessary for the Website operational purposes.
              This includes, but is not limited to the right to store Your Content
              in our database or any other storage and make backups;
              show it to you and other users; parse it into a search index or otherwise analyze it on our servers.</p>

            <p>This license does not grant us the right to sell Your Content or otherwise distribute or use it outside of our
              provision of the Service.</p>

            <p>Privacy aspects of storage and processing of Your Content are covered by the
              <a :href="routes.ABOUT_PRIVACY">Privacy Policy</a>.
            </p>
          </dd>

          <dt>License grant to other users</dt>
          <dd>
            <p>By sharing any part of Your Content with other users you grant other users the right to view,
              download and modify Your Content, if permitted by access control permissions set by You on Your Content.</p>
          </dd>

          <dt>We may remove content</dt>
          <dd>
            <p>We do not pre-screen User-Generated Content, but we have the right
              (though not the obligation) to refuse or remove any User-Generated Content
              that, in our sole discretion, violates any GanttProject Cloud terms or policies.
            </p>
          </dd>
        </dl>


        <h4 id="pricing">Pricing and Payment Terms</h4>
        <dl>
          <dt>Pricing</dt>
          <dd>
            <p>GanttProject Cloud is a pay-as-you-go service. You buy an amount of credits in advance and use the
              purchased credits to pay for the billable activities of Your team members.</p>
            <p>The purchased credits are accumulated in your Billing Accounts balance. You can add credit to any
              Billing Account at any moment.</p>
            <p>Every Billing Account may be associated with any number of teams. Every team has a billing cycle,
              and the billable activities of the team members are charged against the billing account associated with
              the team at the end of the team billing cycle.
            </p>
            <p>The following user activities are billable:</p>
              <ul>
                <li>Access (reading or writing) to project files from the desktop GanttProject application.</li>
              </ul>
            <p>As new features are added to GanttProject Cloud, more activities may become billable.</p>

            <p>A team member is considered as active in the current billing cycle if he or she makes at least one billable activity.
              At the end of the billing cycle we charge 1 credit per each active team member, except for the team owner.
              We may also provide, regularly or occasionally, and at our sole discretion,
              certain amount of free credit per team. For the Billing Accounts of users who signed up before 4 October 2026 00:00 UTC,
              whenever a team usage at the end of a billing cycle becomes non-zero, we exclude 1 credit from its chargeable amount.
            </p>
            <p>Should the billing account balance become negative, we may reject billable activities
            from the members of all teams associated with that billing account.</p>
            <p>Unless otherwise agreed between You and Us, all fees payable under these terms are in Euros.</p>

            <p>As of 16 April 2021, the price of one credit is 1 EUR and the minimum top-up amount is 10 EUR.
              This price includes VAT if applicable at rates of your country of residence. Once purchased at the listed price, credit
              remains valid until it is used. However, purchase prices of subsequent credits may
              change.
            </p>
            <p>Users who sign up on or after 4 October 2026 00:00 UTC get 2 credits for free on their Billing Account.
              Users who signed up before that moment got 5 free credits.
            </p>

            <p>We'll keep you informed with all available means and in reasonable
              timing about any changes in the pricing scheme.</p>
          </dd>

          <dt>Merchant of Record</dt>
          <dd>Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for
            all our orders. Paddle provides all customer service inquiries and handles returns.</dd>

          <dt>Refund Policy</dt>
          <dd><p>We provide full refund within 30 days after purchasing any credit. We refund the whole purchase,
            and partial refunds are not possible.
            To request refunding please send an email to <code>{{emails.CONTACT}}</code>
            and attach a copy of your receipt or invoice. After refunding the billing account balance is decremented
            by the sum of refund.</p></dd>
        </dl>

        <h4 id="cancellation">Cancellation and Termination</h4>
        <dl>
          <dt>Account cancellation</dt>
          <dd>
            <p>You can cancel your account at any moment by sending an email with your account id to
              <code>{{emails.CONTACT}}</code>. We process cancellation requests within 72 hours.
              Upon cancellation, Your Content is deleted from the serving part of the Website, however, copies of Your Content
              may remain in backups for some period, as specified in the <a :href="routes.ABOUT_PRIVACY">Privacy Policy</a></p>
          </dd>

          <dt>We may terminate your account</dt>
          <dd>
            <p>We reserve the right to suspend or terminate your access to our Service if you're in breach of these Terms;
              you're using the Services in a manner that would cause a real risk of harm or loss to us or other users,
              including, but not limited to financial and reputational risks.</p>

            <p>We'll provide you with reasonable advance notice via the email address
              associated with your account to solve the issue which caused us to contact
              you. If after such notice you fail to take the
              steps we ask of you, we'll terminate or suspend your access to the Service.</p>

            <p>We won't provide notice before termination if
              doing so would cause us legal liability or compromise our ability to
              provide the Services to our other users; or it is prohibited by law;
              or in case of any other reason which requires immediate action.</p>
          </dd>
        </dl>

        <h4>Disclaimer of Warranties</h4>
        <dl>
          <dt>Service is provided AS-IS</dt>
          <dl>
            <p>We provide the Website and the Service “as is” and “as available,” without warranty of any kind.
              Without limiting this, we expressly disclaim all warranties, whether express, implied or statutory,
              regarding the Website and the Service including without limitation any warranty of merchantability,
              fitness for a particular purpose, title, security, accuracy and non-infringement.

              We do not warrant that the Service will meet your requirements; that the Service will be
              uninterrupted, timely, secure, or error-free; that the information provided through the Service is
              accurate, reliable or correct; that any defects or errors will be corrected; that the Service will
              be available at any particular time or location; or that the Service is free of viruses or other
              harmful components. You assume full responsibility and risk of loss resulting from your interaction
              with the Website and Service usage.</p>
          </dl>

          <dt>Best effort maintenance</dt>
          <p>We will take appropriate actions on recovering service and data and will
            provide service status updates in <a href="https://twitter.com/ganttproject">our Twitter</a>
            in case of failures. We'll also keep you informed in advance about any planned downtime by posting updates on
            Twitter and through the Website notification channels.</p>
        </dl>

        <h4>Limited Liability</h4>
        <p>
          To the fullest extent permitted by applicable law in no event will BarD Software s.r.o.
          or any of its affiliates, its suppliers or licensors, be liable with respect to
          any subject matter of this Agreement under any contract, negligence, strict
          liability or other legal or equitable theory for:
          (i) any special, incidental or consequential damages, whether or not BarD Software s.r.o. has been warned of the
          possibility of such damages;
          (ii) the cost of procurement for substitute products or services;
          (iii) for interruption of use or loss or corruption of data;
          (iv) any loss of profits, revenue, or loss or corruption of data;
          or (v) for any damages whatsoever. BarD Software s.r.o. shall have no liability for any failure or delay due to matters beyond their reasonable control.
        </p>


        <h4>Changes to Terms</h4>
        <p>We reserve the right, at our sole discretion, to modify or change any part of these Terms of Service at any time.
          We will take reasonable efforts to notify you in case of any significant changes to this Agreement, such as price
          changes or changes which restrict your rights, before changes take effect by
          email address which you provide in your account settings or through the notification channels in the Website.
          In case of less significant modifications, your continued use of the Website constitutes agreement to our revisions of these Terms of Service. </p>

        <h5>Change History</h5>
        <dl>
          <dt>4 October 2026</dt>
          <dd>Effective from 4 October 2026 00:00 UTC, the users who sign up are no longer granted the free monthly credit
            of 1 credit per team, and the free credit granted on signup is decreased from 5 to 2 credits.
            Users who signed up before that moment keep the free monthly credit.
          </dd>
          <dt>16 April 2021</dt>
          <dd>Pricing section updated to be easier to understand. The phrase "Credit is charged on the first project access"
          replaced with "activities of the team members are charged against the billing account associated with
            the team at the end of the team billing cycle". No other significant changes.
          </dd>
        </dl>
        <h4>Contacts</h4>
        <p>The Website and Service are provided by BarD Software s.r.o., a limited liability company registered
          in Czech Republic. You can reach us by email <code>{{emails.CONTACT}}</code></p>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import Header from '@/components/Header.vue';

@Component({
  components: {Header}
})
export default class AboutTos extends Vue {
  private routes = {
    ABOUT_CONTACTS: "/about/contacts",
    ABOUT_TOS: "/about/tos",
    ABOUT_PRIVACY: "/about/privacy"
  }
  private emails = {
    CONTACT: "contact@ganttproject.cloud"
  }
}
</script>

<style lang="scss">
@import 'sass/about_tos';

</style>
